/*
 * Squad rules, mirrored from angular_flask/helpers/draft.py.
 *
 * The server is the authority — it revalidates everything on save, and a
 * draft that only passes here will still be rejected. These run in the
 * browser so the builder can grey out an illegal pick at the moment it
 * would be made rather than reporting it after a round trip.
 *
 * Costs are in tenths of a million throughout, matching FPL's own
 * `now_cost`, so budget arithmetic is integer and never drifts.
 */

export const BUDGET = 1000;
export const SQUAD_SIZE = 15;
export const XI_SIZE = 11;
export const MAX_PER_CLUB = 3;
export const MAX_NAME_LENGTH = 60;

/* Exact composition of the 15. */
export const SQUAD_QUOTA = { GKP: 2, DEF: 5, MID: 5, FWD: 3 };

/* A legal starting XI. */
export const XI_MIN = { GKP: 1, DEF: 3, MID: 2, FWD: 1 };
export const XI_MAX = { GKP: 1, DEF: 5, MID: 5, FWD: 3 };

export const POSITIONS = ['GKP', 'DEF', 'MID', 'FWD'];

export const POSITION_LABELS = {
	GKP: 'Goalkeepers',
	DEF: 'Defenders',
	MID: 'Midfielders',
	FWD: 'Forwards',
};

/*
 * The shape is how many of each position are on the pitch. There is no
 * formation control: it starts here and only ever moves because someone
 * substituted a defender for a midfielder, exactly as it does in FPL.
 */
export const DEFAULT_SHAPE = { GKP: 1, DEF: 4, MID: 4, FWD: 2 };

export function formationLabel(shape) {
	return `${shape.DEF}-${shape.MID}-${shape.FWD}`;
}

export function isLegalShape(shape) {
	const total = POSITIONS.reduce((sum, position) => sum + shape[position], 0);
	if (total !== XI_SIZE) return false;
	return POSITIONS.every(
		(position) => shape[position] >= XI_MIN[position] && shape[position] <= XI_MAX[position],
	);
}

/* The shape that results from taking one player off in `out` and bringing
 * one on in `into`. Same position is a like-for-like swap. */
export function shapeAfterSwap(shape, out, into) {
	if (out === into) return shape;
	return { ...shape, [out]: shape[out] - 1, [into]: shape[into] + 1 };
}

export function formatCost(tenths) {
	return `£${(tenths / 10).toFixed(1)}m`;
}

/* The squad is four position-keyed arrays of fixed quota length. Entries
 * before the shape's count for that position are on the pitch; the rest
 * are substitutes. Nulls are unfilled slots. */
export function emptySquad() {
	return {
		GKP: Array(SQUAD_QUOTA.GKP).fill(null),
		DEF: Array(SQUAD_QUOTA.DEF).fill(null),
		MID: Array(SQUAD_QUOTA.MID).fill(null),
		FWD: Array(SQUAD_QUOTA.FWD).fill(null),
	};
}

export function allPlayers(squad) {
	return POSITIONS.flatMap((position) => squad[position]).filter(Boolean);
}

export function totalCost(squad) {
	return allPlayers(squad).reduce((sum, player) => sum + player.cost, 0);
}

export function clubCounts(squad) {
	const counts = {};
	for (const player of allPlayers(squad)) {
		counts[player.team_name] = (counts[player.team_name] || 0) + 1;
	}
	return counts;
}

export function isComplete(squad) {
	return allPlayers(squad).length === SQUAD_SIZE;
}

export function splitSquad(squad, shape) {
	const starting = {};
	const benched = {};
	for (const position of POSITIONS) {
		starting[position] = squad[position].slice(0, shape[position]);
		benched[position] = squad[position].slice(shape[position]);
	}
	return { starting, benched };
}

/* The benched outfielders, in no particular order — who is on the bench,
 * not the order they come on in. */
function benchedOutfield(squad, shape) {
	const { benched } = splitSquad(squad, shape);
	return [...benched.DEF, ...benched.MID, ...benched.FWD].filter(Boolean);
}

/*
 * Keep a bench order valid as the squad changes.
 *
 * `sequence` is element ids in substitution priority. Players who have
 * left the bench drop out and new ones join the end, so an order someone
 * arranged survives an unrelated transfer instead of resetting.
 */
export function syncBenchSequence(squad, shape, sequence = []) {
	const onBench = benchedOutfield(squad, shape);
	const ids = new Set(onBench.map((player) => player.element));
	const kept = sequence.filter((element) => ids.has(element));
	const added = onBench
		.map((player) => player.element)
		.filter((element) => !kept.includes(element));
	return [...kept, ...added];
}

/*
 * Bench order: reserve keeper first, then the outfield substitutes in the
 * order the author put them in.
 *
 * The server requires slot one to be the goalkeeper — an outfield
 * substitution can never bring on a keeper, so the reserve sits outside
 * the priority ordering rather than at a place within it.
 */
export function benchOrder(squad, shape, sequence = []) {
	const { benched } = splitSquad(squad, shape);
	const byElement = new Map(
		benchedOutfield(squad, shape).map((player) => [player.element, player]),
	);
	const ordered = syncBenchSequence(squad, shape, sequence)
		.map((element) => byElement.get(element))
		.filter(Boolean);
	return [...benched.GKP.filter(Boolean), ...ordered];
}

export function toPayload(squad, shape, name, benchSequence = []) {
	const { starting } = splitSquad(squad, shape);
	const xi = POSITIONS.flatMap((position) => starting[position]);
	return {
		name: name.trim().slice(0, MAX_NAME_LENGTH) || 'Untitled draft',
		squad: allPlayers(squad).map((player) => player.element),
		xi: xi.filter(Boolean).map((player) => player.element),
		bench: benchOrder(squad, shape, benchSequence)
			.filter(Boolean)
			.map((player) => player.element),
	};
}

/* The first free slot for a position, or null when that line is full.
 * Pitch slots come first, so picks fill the XI before the bench. */
export function firstFreeSlot(squad, position) {
	const index = squad[position].findIndex((occupant) => !occupant);
	return index === -1 ? null : { position, index };
}

/*
 * Grow the shape so the XI holds as many of the picked players as it
 * legally can.
 *
 * Without this the shape is fixed at kickoff, so a third forward lands on
 * the bench even though the XI still has four empty defender slots. Here
 * the extra pitch slot is taken from a line that has one going spare, so
 * the XI fills up before the bench does and nobody already on the pitch is
 * demoted to pay for it.
 *
 * A move needs a position with someone waiting on the bench, and a donor
 * with an *unused* pitch slot and room above its minimum. Repeated until
 * no move is available, since one pick can enable the next.
 */
export function fillXiFirst(squad, shape) {
	const count = (position) => squad[position].filter(Boolean).length;
	let next = { ...shape };

	for (;;) {
		const position = POSITIONS.find(
			(candidate) => count(candidate) > next[candidate] && next[candidate] < XI_MAX[candidate],
		);
		if (!position) return next;

		const donor = POSITIONS.find(
			(candidate) =>
				candidate !== position &&
				next[candidate] > XI_MIN[candidate] &&
				count(candidate) < next[candidate],
		);
		if (!donor) return next;

		next = { ...next, [position]: next[position] + 1, [donor]: next[donor] - 1 };
	}
}

/*
 * Why a given player cannot be picked, or null if they can.
 *
 * Only structural limits block a pick: a squad physically cannot hold six
 * defenders or four Arsenal players, so those are refused at the point of
 * clicking. The budget is not one of these — going over is a position you
 * are allowed to be in and work back from, so it is shown on the bar and
 * enforced at publish instead.
 */
export function rejectionReason(squad, player) {
	if (allPlayers(squad).some((p) => p.element === player.element)) {
		return 'Picked';
	}

	if (!firstFreeSlot(squad, player.position)) {
		return `${player.position} full`;
	}

	if ((clubCounts(squad)[player.team_name] || 0) >= MAX_PER_CLUB) {
		return `${MAX_PER_CLUB} from ${player.team_name}`;
	}

	return null;
}

/*
 * What stops this squad being publishable, or [] if nothing does.
 *
 * Being over budget is not on the list. £100.0m is a starting figure, not
 * a cap — team value rises through a season — so the bar reports the
 * overspend and publishing goes ahead regardless. What remains here is
 * structural: a squad that is short of players, or has too many from one
 * club, is not a squad.
 */
export function outstanding(squad) {
	const problems = [];

	const missing = SQUAD_SIZE - allPlayers(squad).length;
	if (missing > 0) problems.push(`${missing} to pick`);

	for (const [club, count] of Object.entries(clubCounts(squad))) {
		if (count > MAX_PER_CLUB) {
			problems.push(`${count} from ${club}`);
		}
	}

	return problems;
}
